import { useState } from "react";
import classNames from "classnames";
import { Modal } from "../ui/Modal";
import { newViewId, type SavedView } from "../../lib/viewEngine";

interface Props {
  views: SavedView[];
  activeId: string;
  onSelect: (id: string) => void;
  onAdd: (view: SavedView) => void;
  onRename: (id: string, name: string) => void;
  onRemove: (id: string) => void;
}

/** Onglets de vues sauvegardées — renommables (double-clic), supprimables, créables. */
export function ViewTabs({
  views,
  activeId,
  onSelect,
  onAdd,
  onRename,
  onRemove,
}: Props) {
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<SavedView | null>(null);

  const commitRename = (id: string) => {
    const v = draft.trim();
    if (v) onRename(id, v);
    setEditing(null);
  };

  return (
    <div className="view-tabs" role="tablist">
      {views.map((v) => (
        <span
          key={v.id}
          className={classNames("view-tab", { active: v.id === activeId })}
        >
          {editing === v.id ? (
            <input
              className="view-tab-input"
              value={draft}
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => commitRename(v.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitRename(v.id);
                if (e.key === "Escape") setEditing(null);
              }}
              aria-label="Renommer la vue"
            />
          ) : (
            <button
              type="button"
              role="tab"
              aria-selected={v.id === activeId}
              className="view-tab-btn"
              onClick={() => onSelect(v.id)}
              onDoubleClick={() => {
                setEditing(v.id);
                setDraft(v.name);
              }}
              title="Double-clic pour renommer"
            >
              {v.name}
            </button>
          )}
          {v.id === activeId && views.length > 1 && (
            <button
              type="button"
              className="view-tab-close"
              aria-label={`Supprimer la vue ${v.name}`}
              onClick={() => setConfirmDelete(v)}
            >
              ×
            </button>
          )}
        </span>
      ))}
      <button
        type="button"
        className="view-tab-add"
        aria-label="Nouvelle vue"
        onClick={() =>
          onAdd({
            id: newViewId(),
            name: "Nouvelle vue",
            layout: "list",
            groupBy: "none",
            sort: [{ field: "due", dir: "asc" }],
            filter: { operator: "and", conditions: [] },
            hidden: [],
          })
        }
      >
        +
      </button>

      <Modal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Supprimer la vue"
        footer={
          <>
            <button className="btn" onClick={() => setConfirmDelete(null)}>
              Annuler
            </button>
            <button
              className="btn danger"
              onClick={() => {
                if (confirmDelete) onRemove(confirmDelete.id);
                setConfirmDelete(null);
              }}
            >
              Supprimer
            </button>
          </>
        }
      >
        La vue « {confirmDelete?.name} » sera supprimée. Les tâches ne sont pas
        affectées.
      </Modal>
    </div>
  );
}
